import api from "./api";

/**
 * Adds a count property to each object in the tree structure
 * @param {Array} items - Array of objects with parent-child relationships
 * @returns {Array} - The same array with count properties added
 */
function addCountToTree(items) {
  const itemMap = new Map();
  items.forEach((item) => {
    itemMap.set(item._id, {
      ...item,
      children: [],
      status: item.status || "A", // Ensure status is preserved
    });
  });

  // Second pass: Build the parent-child relationships
  const rootNodes = [];

  items.forEach((item) => {
    const mappedItem = itemMap.get(item._id);

    // Check if this item has a parent (sub_material)
    if (item.sub_material && typeof item.sub_material === "object") {
      const parentId = item.sub_material._id;
      const parent = itemMap.get(parentId);

      if (parent) {
        // Add this item as a child of its parent
        parent.children.push(mappedItem);
      } else {
        // If parent not found (shouldn't happen with valid data)
        rootNodes.push(mappedItem);
      }
    } else {
      // No parent, so it's a root node
      rootNodes.push(mappedItem);
    }
  });

  // Recursive function to calculate count for each node
  function calculateCount(node) {
    if (node.children.length === 0) {
      // Leaf node has count = 1 if activated
      node.count = node.status === "A" ? 1 : 0;
      return node.count;
    } else {
      // Parent node count is sum of children counts
      let totalCount = 0;

      for (const child of node.children) {
        totalCount += calculateCount(child);
      }

      node.count = totalCount;
      return totalCount;
    }
  }

  // Calculate counts starting from each root node
  rootNodes.forEach((node) => {
    calculateCount(node);
  });
  // console.log(`rootNodes:`, rootNodes);

  // Return the updated array (remove the temporary children arrays)
  return items.map((item) => {
    const updatedItem = { ...itemMap.get(item._id) };
    delete updatedItem.children;
    return updatedItem;
  });
}

/**
 * Transforms a flat array of materials into a hierarchical tree structure
 * @param {Array} data - Array of material objects
 * @returns {Array} - Hierarchical tree structure
 */
const transformToTree = (data) => {
  const materialMap = new Map();
  data.forEach((item) => {
    materialMap.set(item._id, {
      id: item._id,
      category: item.category || "",
      description: item.description || "",
      unit: item.unit || "",
      point: item.point ?? 0,
      status: item.status || "A", // Add this line
      count: item.count ?? 1,
      children: [],
    });
  });
  // console.log(`transformToTree materialMap:`, materialMap);

  // Build the tree structure
  const rootMaterials = [];

  data.forEach((item) => {
    const material = materialMap.get(item._id);

    // If this material has a sub_material, it's a child
    if (item.sub_material && item.sub_material._id) {
      const parentId = item.sub_material._id;
      const parent = materialMap.get(parentId);

      if (parent) {
        parent.children.push(material);
      }
    } else {
      // No sub_material means it's a root node
      rootMaterials.push(material);
    }
  });

  // Return rootMaterials sorted by category for level recursively
  rootMaterials.sort((a, b) => a.category.localeCompare(b.category));
  rootMaterials.forEach((material) => {
    material.children.sort((a, b) => a.category.localeCompare(b.category));
  });
  // console.log(`transformToTree rootMaterials:`, rootMaterials);

  return rootMaterials;
};

export const materialService = {
  getMaterial: async () => {
    try {
      // console.log("Calling get Material api");

      const response = await api.get(`/mapi/material/getMaterialList`);

      return response.data;
    } catch (error) {
      console.error(
        "Error fetching Material list data:",
        error.response?.data || error.message
      );
      throw error;
    }
  },

  addMaterial: async (materialData) => {
    try {
      const requestData = {
        Category: materialData.name, // Assuming "name" is the category
        Description: materialData.description,
        Unit: materialData.unit,
        SubMaterialId: materialData.subMaterialId,
        Point: Number(materialData.pointsPerUnit), // Ensure it's a number
      };
      console.log("Raw materialData received:", materialData);
      console.log("Transformed requestData for API:", requestData);

      const response = await api.post(
        `/mapi/material/addMaterial`,
        requestData
      );
      return response.data;
    } catch (error) {
      console.error(
        "Error adding new Material:",
        error.response?.data || error.message
      );
      throw error;
    }
  },

  editMaterialDetails: async (materialData) => {
    try {
      console.log("Editing Material:", materialData);

      const response = await api.post(
        `/mapi/material/editMaterial`,
        materialData
      );
      return response.data;
    } catch (error) {
      console.error(
        "Error updating Material Data details:",
        error.response?.data || error.message
      );
      throw error;
    }
  },

  removeMaterial: async (materialId, operatorId) => {
    try {
      const requestData = {
        OperatorId: operatorId, // Pass in the operator ID from the store or context
        MaterialId: materialId, // Material ID to delete
      };

      console.log("Removing Material with request data:", requestData);

      const response = await api.post(
        `/mapi/material/removeMaterial`,
        requestData
      );
      return response.data;
    } catch (error) {
      console.error(
        "Error removing Material:",
        error.response?.data || error.message
      );
      throw error;
    }
  },

  getTreeData: (data) => {
    const countedData = addCountToTree(data);
    const formattedData = transformToTree(countedData);
    // console.log(`materialService countedData:`, countedData);
    // console.log(`materialService formattedData:`, formattedData);
    return formattedData;
  },
};
